import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

type Side = 'top' | 'bottom' | 'right'

/**
 * A hover/focus tooltip for a short label on an icon or truncated value — the accessible replacement for the
 * browser's native `title` attribute, which is unstyled, slow to appear, and never reachable by keyboard.
 *
 * The trigger keeps its own layout (this only wraps it to anchor the tooltip and wire up the ARIA relationship);
 * pass a focusable element (or one that already has `tabIndex`) so keyboard users can reach it too.
 */
export function Tooltip({
  label,
  side = 'top',
  singleLine = false,
  delay = 0,
  fill = false,
  disabled = false,
  children,
}: {
  label: ReactNode
  side?: Side
  /** Keep the label on one line, cut with an ellipsis when it does not fit. */
  singleLine?: boolean
  /** Milliseconds the pointer must rest on the trigger before the tooltip appears; keyboard focus shows it at once. */
  delay?: number
  /** Let the trigger take the full width of its container, for a row or a button that fills it. */
  fill?: boolean
  /** Never show the tooltip, e.g. while a menu opened from the trigger is covering the same spot. */
  disabled?: boolean
  children: ReactNode
}) {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ left: 0, top: 0 })
  const id = useId()
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLSpanElement>(null)
  const centerRef = useRef(0)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const showTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () => () => {
      clearTimeout(hideTimer.current)
      clearTimeout(showTimer.current)
    },
    [],
  )

  const show = () => {
    clearTimeout(hideTimer.current)
    clearTimeout(showTimer.current)
    const bounds = triggerRef.current?.getBoundingClientRect()
    if (bounds) {
      centerRef.current = bounds.left + bounds.width / 2
      setPosition(
        side === 'right'
          ? { left: bounds.right + 8, top: bounds.top + bounds.height / 2 }
          : {
              left: centerRef.current,
              top: side === 'top' ? bounds.top - 6 : bounds.bottom + 6,
            },
      )
    }
    if (!disabled) setVisible(true)
  }
  const showAfterDelay = () => {
    clearTimeout(hideTimer.current)
    clearTimeout(showTimer.current)
    if (delay > 0) showTimer.current = setTimeout(show, delay)
    else show()
  }
  // The tooltip is centred on its trigger; once it is shown, its own width decides how far it may move so that
  // it never runs off either edge of the screen.
  useLayoutEffect(() => {
    if (!visible || side === 'right') return
    const width = tipRef.current?.getBoundingClientRect().width ?? 0
    const margin = 12
    const left = Math.min(
      Math.max(centerRef.current, width / 2 + margin),
      window.innerWidth - width / 2 - margin,
    )
    setPosition((current) =>
      Math.abs(current.left - left) < 0.5 ? current : { ...current, left },
    )
  }, [visible, label, side])
  // A short delay survives the pointer briefly crossing a gap (e.g. moving toward the tooltip itself).
  const hide = () => {
    clearTimeout(showTimer.current)
    hideTimer.current = setTimeout(() => setVisible(false), 60)
  }

  return (
    <span
      ref={triggerRef}
      className={`group/tooltip relative min-w-0 ${fill ? 'flex w-full' : 'inline-flex'}`}
      onPointerEnter={showAfterDelay}
      onPointerLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span aria-describedby={visible ? id : undefined} className="contents">
        {children}
      </span>
      {createPortal(
        <span
          ref={tipRef}
          role="tooltip"
          id={id}
          style={{ left: position.left, top: position.top }}
          className={`pointer-events-none fixed z-[100] max-w-[min(24rem,calc(100vw-1.5rem))] rounded-md border border-border-strong bg-raised px-2 py-1 text-[11px] font-medium text-secondary shadow-lg ${singleLine ? 'truncate whitespace-nowrap' : 'whitespace-normal'} transition-opacity duration-100 ${visible && !disabled ? 'opacity-100' : 'opacity-0'} ${side === 'top' ? '-translate-x-1/2 -translate-y-full' : side === 'right' ? '-translate-y-1/2' : '-translate-x-1/2'}`}
        >
          {label}
        </span>,
        document.body,
      )}
    </span>
  )
}
